import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ResponsiveContainer, ResponsiveContent, ResponsiveHeader, ResponsiveFooter } from '../ui/ResponsiveLayout';
import BrandSymbol from '../ui/BrandSymbol';
import AttorneyMarketplaceModal from '../ui/AttorneyMarketplaceModal';
import { useSessionPersistence } from '../../hooks/useSessionPersistence';

const Frame182: React.FC = () => {
  const navigate = useNavigate();
  const [showAttorneyMarketplace, setShowAttorneyMarketplace] = useState(false);
  const { session, completeTask } = useSessionPersistence();

  // Asset calculation logic (same as Frame196)
  const properties = session.taskProgress.properties?.answers?.properties || [];
  let assetTotal = 0;
  properties.forEach((property: unknown) => {
    let netWorth = 0, mortgage = 0;
    if (typeof property.netWorth === 'number') netWorth = property.netWorth;
    else if (typeof property.netWorth === 'string') {
      const num = Number(property.netWorth.replace(/[^\d.-]/g, ''));
      if (!isNaN(num)) netWorth = num;
    }
    if (typeof property.mortgage === 'number') mortgage = property.mortgage;
    else if (typeof property.mortgage === 'string') {
      const num = Number(property.mortgage.replace(/[^\d.-]/g, ''));
      if (!isNaN(num)) mortgage = num;
    }
    const equity = netWorth - mortgage;
    if (!isNaN(equity)) assetTotal += equity;
  });
  const formattedAssetTotal = assetTotal.toLocaleString('en-US', { style: 'currency', currency: 'USD' });

  const handleComplete = async () => {
    console.log('[Frame182] Marking professional support task as complete');
    await completeTask('professional');
    console.log('[Frame182] Professional support task marked as complete');
    
    // CRITICAL FIX: Also complete the inventory task since this is the final step of the inventory workflow
    console.log('[Frame182] Marking inventory task as complete');
    await completeTask('inventory');
    console.log('[Frame182] Inventory task marked as complete');
    
    navigate('/frame-140');
  };

  const handleBack = () => {
    navigate('/frame-190');
  };

  const handleLearnMore = () => {
    alert('Opening living trust information...');
  };

  return (
    <ResponsiveContainer background="stone">
      <ResponsiveContent>
        
        {/* Centered Logo */}
        <div className="flex justify-center mb-8">
          <BrandSymbol size="3xl" variant="dark" showText={true} />
        </div>

        {/* Header */}
        <ResponsiveHeader 
          title="Consider a living trust"
          onClose={handleBack}
        />

        {/* Main Content */}
        <div className="flex-1 space-y-8">
          
          {/* Asset Information and Trust Recommendation */}
          <div className="max-w-4xl space-y-6">
            <div className="text-stone-800 text-lg font-medium font-['Work_Sans'] leading-relaxed">
              <p>
                Your total assets are currently estimated at{' '}
                <span className="font-bold">{formattedAssetTotal}</span>
                . Creating a living trust could save your heirs thousands of dollars in taxes and fees, and protect your assets from{' '}
                <button 
                  className="underline hover:text-stone-600 transition-colors"
                  onClick={() => alert('Opening probate information...')}
                >
                  probate
                </button>
                .
              </p>
              <p className="mt-4">
                All End of an Era users have access to special offers from our partners, who can help you craft a living trust to protect you and your loved ones.
              </p>
            </div>

            {/* Special Offers Button */}
            <div>
              <button 
                onClick={() => setShowAttorneyMarketplace(true)}
                className="px-6 py-3 bg-green-700 hover:bg-green-800 rounded inline-flex justify-center items-center gap-2.5 transition-colors"
              >
                <span className="text-center text-white text-base font-medium font-['Work_Sans']">
                  See special offers →
                </span>
              </button>
            </div>
          </div>

          {/* Learn Banner */}
          <div className="bg-orange-100 rounded p-3 max-w-md">
            <div className="flex items-center gap-3">
              <div className="w-3.5 h-3.5 bg-orange-400 rounded-full flex-shrink-0" />
              <p className="text-orange-700 text-sm font-medium font-['Work_Sans']">
                Learn:{' '}
                <button 
                  className="underline hover:text-orange-800 transition-colors"
                  onClick={handleLearnMore}
                >
                  What is a living trust? →
                </button>
              </p>
            </div>
          </div>
        </div>

      </ResponsiveContent>

      {/* Footer */}
      <ResponsiveFooter
        title="Inventory of Assets"
        subtitle="Approximately 20 minutes"
        progressPercent={60}
        onNext={handleComplete}
        onBack={handleBack}
        nextText="Complete →"
        backText="← Back"
      />

      {/* Attorney Marketplace Modal */}
      <AttorneyMarketplaceModal 
        open={showAttorneyMarketplace} 
        onClose={() => setShowAttorneyMarketplace(false)} 
      />
    </ResponsiveContainer>
  );
};

export default Frame182; 